import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import type { Step } from "react-joyride";

import { findDemoCondition, getOfferedOperators, type DemoCondition } from "./demoCondition";
import { SEGMENT_BUILDER_TOUR, TOUR_STEP_ID } from "./segmentBuilderTour";

import { OperatorValue } from "@/config/operatorConfig";
import { RuleSummary } from "@/features/segments/builder/components/RuleSummary";
import { getOperatorLabel } from "@/features/segments/builder/operatorLabels";
import type { FormRule } from "@/features/segments/builder/schemas";
import type { SegmentField } from "@/features/segments/types";

const SUMMARY_VALUE_LIMIT = 2;

const describeCondition = (
  t: TFunction,
  { field, operator, values }: DemoCondition,
  fields: SegmentField[],
  isInverted: boolean,
) => {
  const hiddenCount = values.length - SUMMARY_VALUE_LIMIT;
  const shownValues = values.slice(0, SUMMARY_VALUE_LIMIT).join(", ");
  return {
    count: values.length,
    field: fields.find((candidate) => candidate.value === field)?.label ?? field,
    operator: getOperatorLabel(t, operator, isInverted).text,
    values:
      hiddenCount > 0
        ? `${shownValues} ${t("admin.segments.builder.moreValues", { count: hiddenCount })}`
        : shownValues,
  };
};

const renderOperatorList = (operators: OperatorValue[], describe: (operator: OperatorValue) => ReactNode) => (
  <ul className="flex list-disc flex-col gap-1 pl-4">
    {operators.map((operator) => (
      <li key={operator}>{describe(operator)}</li>
    ))}
  </ul>
);

type SegmentStepContentParams = {
  stepIds: string[];
  t: TFunction;
  readOnly: boolean;
  segmentCount?: number;
  firstRule?: FormRule;
  fields: SegmentField[];
};

export const createSegmentStepContent =
  ({ stepIds, t, readOnly, segmentCount, firstRule, fields }: SegmentStepContentParams) => {
    const offeredOperators = getOfferedOperators(fields);
    const demoCondition = findDemoCondition(firstRule, fields);
    const demoFieldOperators =
      (demoCondition && fields.find((field) => field.value === demoCondition.field)?.operators) ?? offeredOperators;
    const operatorKey = (operator: OperatorValue) =>
      `${SEGMENT_BUILDER_TOUR.copyKey}.step.${TOUR_STEP_ID.OPERATORS}.operator.${operator}`;

    return (id: string): Step["content"] => {
      const stepKey = (part: string) => `${SEGMENT_BUILDER_TOUR.copyKey}.step.${id}.${part}`;
      const explainOperators = (copyKey: (operator: OperatorValue) => string) =>
        renderOperatorList(demoFieldOperators, (operator) => (
          <>
            <strong>{getOperatorLabel(t, operator, false).text}</strong>: {t(copyKey(operator))}
          </>
        ));

      switch (id) {
      case TOUR_STEP_ID.TABS:
        return t(stepKey(stepIds.includes(TOUR_STEP_ID.HISTORY_TIMELINE) ? "body" : "bodyNoHistory"));
      case TOUR_STEP_ID.VACANCIES:
        return t(stepKey(stepIds.includes(TOUR_STEP_ID.SAVE) ? "body" : "bodyNoEdit"));
      case TOUR_STEP_ID.LIVE_COUNT:
        if (segmentCount === undefined) return t(stepKey("bodyNoCount"));
        return t(stepKey("body"), { count: segmentCount });
      case TOUR_STEP_ID.MATCH_IN_RULE:
        return (
          <div className="flex flex-col gap-2">
            <p>{t(stepKey(readOnly ? "bodyReadonly" : "body"))}</p>
            {firstRule && firstRule.conditions.length > 0 && (
              <p>
                <span className="text-muted-foreground">{t(stepKey("yourRule"))} </span>
                <RuleSummary rule={firstRule} fields={fields} />
              </p>
            )}
          </div>
        );
      case TOUR_STEP_ID.OPERATORS:
        return (
          <div className="flex flex-col gap-2">
            <p>{t(stepKey("intro"))}</p>
            {explainOperators(operatorKey)}
            <p>{t(stepKey("fieldNote"))}</p>
          </div>
        );
      case TOUR_STEP_ID.CONDITION_EDITOR:
      case TOUR_STEP_ID.FIELD:
        if (!demoCondition) return t(stepKey("body"));
        return t(stepKey("bodyYours"), describeCondition(t, demoCondition, fields, false));
      case TOUR_STEP_ID.VALUES:
        return (
          <div className="flex flex-col gap-2">
            <p>{t(stepKey("intro"))}</p>
            {explainOperators((operator) => stepKey(`value.${operator}`))}
          </div>
        );
      case TOUR_STEP_ID.EXCLUDE:
        return (
          <div className="flex flex-col gap-2">
            <p>{t(stepKey("intro"))}</p>
            {renderOperatorList(demoFieldOperators, (operator) => (
              <>
                {getOperatorLabel(t, operator, false).text} → <strong>{getOperatorLabel(t, operator, true).text}</strong>
              </>
            ))}
            <p>
              {demoCondition
                ? t(stepKey("yourCondition"), describeCondition(t, demoCondition, fields, true))
                : t(stepKey("example"))}
            </p>
          </div>
        );
      case TOUR_STEP_ID.CONDITIONS:
        if (!readOnly) return t(stepKey("body"));
        return (
          <div className="flex flex-col gap-2">
            <p>{t(stepKey("bodyReadonly"))}</p>
            {renderOperatorList(offeredOperators, (operator) => (
              <>
                <strong>{getOperatorLabel(t, operator, false).text}</strong> /{" "}
                <strong>{getOperatorLabel(t, operator, true).text}</strong>: {t(operatorKey(operator))}
              </>
            ))}
            <p>{t(stepKey("readonlyOutro"))}</p>
          </div>
        );
      case TOUR_STEP_ID.COMBINE_GROUPS:
      case TOUR_STEP_ID.EXCEPTIONS:
        return t(stepKey(readOnly ? "bodyReadonly" : "body"));
      default:
        return t(stepKey("body"));
      }
    };
  };
